/*
 * Eventi ecommerce GA4 per GTM (solo noleggi con container GTM attivo).
 *
 * - begin_checkout: appena prima del redirect al gateway di pagamento. Il redirect
 *   attende la conferma di GTM (eventCallback) per max 2 secondi.
 * - purchase: nella pagina di esito positivo, una sola volta per codice ordine.
 *   Gli articoli arrivano dalla copia salvata al begin_checkout (sessionStorage),
 *   in mancanza dal carrello persistito.
 *
 * value = totale noleggio; deposit_paid = importo versato online (caparra).
 * Nessun dato personale del cliente viene inviato.
 */
import { isGtmActiveFor, pushToGtm } from "../hooks/useGoogleTagManager";

const CURRENCY = "EUR";
const CHECKOUT_KEY_PREFIX = "ns_checkout_";
const SENT_KEY = "ns_purchases_sent";
const MAX_SENT = 20;
const REDIRECT_TIMEOUT_MS = 2000;

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function toDateString(value) {
  if (!value) return undefined;
  const d = new Date(value);
  if (isNaN(d.getTime())) return undefined;
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function nomeSettore(settori, idSettore) {
  const s = (settori || []).find((x) => x.idNoloshop === idSettore);
  return s && s.titolo ? s.titolo : undefined;
}

// Converte le righe carrello nel formato ecommerce GA4
export function buildEcommerce(righe, settori) {
  const items = [];
  let value = 0;
  let deposit = 0;

  (righe || []).forEach((r) => {
    const prezzo = round2(r.prezzoApplicato);
    const listino = round2(r.prezzo);
    const categoria = nomeSettore(settori, r.idSettore);
    value += prezzo;
    deposit += (prezzo / 100) * (Number(r.percCaparra) || 0);

    items.push({
      item_id: String(r.idReparto),
      item_name: r.nomeReparto,
      item_category: categoria,
      item_variant: r.misura || undefined,
      price: prezzo,
      discount: listino > prezzo ? round2(listino - prezzo) : undefined,
      quantity: 1,
      rental_days: r.giorni,
      start_date: toDateString(r.dal),
    });

    (r.accessori || []).forEach((a) => {
      const prezzoAcc = round2(a.prezzoApplicato);
      const listinoAcc = round2(a.prezzo);
      value += prezzoAcc;
      deposit += (prezzoAcc / 100) * (Number(a.percCaparra) || 0);

      items.push({
        item_id: `ACC-${a.idNoloShop}`,
        item_name: a.nomeReparto,
        item_category: categoria,
        item_category2: "Accessori",
        price: prezzoAcc,
        discount:
          listinoAcc > prezzoAcc ? round2(listinoAcc - prezzoAcc) : undefined,
        quantity: 1,
        rental_days: r.giorni,
        start_date: toDateString(r.dal),
      });
    });
  });

  return {
    ecommerce: { currency: CURRENCY, value: round2(value), items },
    deposit: round2(deposit),
  };
}

function checkoutKey(codiceNoleggio) {
  return CHECKOUT_KEY_PREFIX + String(codiceNoleggio).toUpperCase();
}

function saveCheckout(codiceNoleggio, data) {
  try {
    window.sessionStorage.setItem(
      checkoutKey(codiceNoleggio),
      JSON.stringify(data)
    );
  } catch (e) {
    // storage non disponibile: al purchase si usera' il carrello
  }
}

function takeCheckout(codiceNoleggio) {
  try {
    const key = checkoutKey(codiceNoleggio);
    const raw = window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function readSent() {
  try {
    const list = JSON.parse(window.localStorage.getItem(SENT_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function markSent(transactionId) {
  try {
    const list = readSent().filter((x) => x !== transactionId);
    list.push(transactionId);
    window.localStorage.setItem(
      SENT_KEY,
      JSON.stringify(list.slice(-MAX_SENT))
    );
  } catch (e) {
    // ignorato
  }
}

/*
 * begin_checkout: chiamare al posto del redirect diretto al gateway.
 * onContinue viene chiamato SEMPRE e una sola volta (subito se GTM non e' attivo,
 * anche se il tracking va in errore). onError riceve l'eventuale errore di tracking.
 */
export function trackBeginCheckout(
  codiceNoleggio,
  righe,
  settori,
  onContinue,
  onError
) {
  let done = false;
  const proceed = () => {
    if (done) return;
    done = true;
    onContinue();
  };

  try {
    sendBeginCheckout(codiceNoleggio, righe, settori, proceed);
  } catch (e) {
    if (onError) {
      try {
        onError(e);
      } catch (e2) {
        // ignorato
      }
    }
    proceed();
  }
}

function sendBeginCheckout(codiceNoleggio, righe, settori, proceed) {
  const data = buildEcommerce(righe, settori);
  saveCheckout(codiceNoleggio, data);

  if (!isGtmActiveFor(codiceNoleggio) || data.ecommerce.items.length === 0) {
    proceed();
    return;
  }

  pushToGtm(codiceNoleggio, { ecommerce: null });
  pushToGtm(codiceNoleggio, {
    event: "begin_checkout",
    ecommerce: data.ecommerce,
    deposit_paid: data.deposit,
    codiceNoleggio,
    eventCallback: proceed,
    eventTimeout: REDIRECT_TIMEOUT_MS,
  });
  // GTM bloccato (es. ad blocker): eventCallback non arriva mai
  setTimeout(proceed, REDIRECT_TIMEOUT_MS + 500);
}

/*
 * purchase: chiamare nella pagina di esito positivo PRIMA di svuotare il carrello.
 * righeCarrello / settori sono il fallback se manca la copia del begin_checkout.
 */
export function trackPurchase(
  codiceNoleggio,
  transactionId,
  righeCarrello,
  settori
) {
  if (!codiceNoleggio || !transactionId) return;
  if (readSent().includes(transactionId)) return;

  let data = takeCheckout(codiceNoleggio);
  if (!data && righeCarrello && righeCarrello.length > 0) {
    data = buildEcommerce(righeCarrello, settori);
  }
  if (!data || data.ecommerce.items.length === 0) return;

  markSent(transactionId);

  // Accodato finche' GTM non e' caricato per il noleggio (pagina appena aperta)
  pushToGtm(codiceNoleggio, { ecommerce: null });
  pushToGtm(codiceNoleggio, {
    event: "purchase",
    ecommerce: { transaction_id: transactionId, ...data.ecommerce },
    deposit_paid: data.deposit,
    codiceNoleggio,
  });
}
